import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Modal, Table, Tag, Button, Alert, Progress, Tooltip, Space, message } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  LoadingOutlined,
  ExclamationCircleTwoTone,
} from '@ant-design/icons';
import OnDemandOrdersService from '../../../services/OnDemandOrdersService';

/**
 * Bulk kit-activation dialog for the Members tab.
 *
 * On open it validates the selected patients (fields present + format + kit code
 * available + not already activated), shows which are ready vs blocked, then —
 * on confirm — activates each ready record one at a time with per-row progress.
 */
const BulkKitActivationModal = ({ visible, onClose, patientIds = [] }) => {
  const [validating, setValidating] = useState(false);
  const [rows, setRows] = useState([]); // validation results
  const [progress, setProgress] = useState({}); // patientId -> { state, message }
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const runValidation = useCallback(async () => {
    if (!patientIds.length) return;
    setValidating(true);
    setDone(false);
    setProgress({});
    try {
      const res = await OnDemandOrdersService.validateKitActivation(patientIds);
      if (res.success) {
        setRows(res.data || []);
      } else {
        message.error(res.message || 'Failed to validate records');
        setRows([]);
      }
    } catch (err) {
      message.error('Failed to validate records');
      setRows([]);
    } finally {
      setValidating(false);
    }
  }, [patientIds]);

  useEffect(() => {
    if (visible) runValidation();
    else {
      setRows([]);
      setProgress({});
      setSending(false);
      setDone(false);
    }
  }, [visible, runValidation]);

  const readyRows = useMemo(
    () => rows.filter((r) => r.ok && !r.alreadyActivated),
    [rows]
  );
  const blockedCount = rows.length - readyRows.length;

  const sentCount = useMemo(
    () => Object.values(progress).filter((p) => p.state === 'success').length,
    [progress]
  );
  const failedCount = useMemo(
    () => Object.values(progress).filter((p) => p.state === 'failed').length,
    [progress]
  );

  const handleSend = async () => {
    setSending(true);
    for (const row of readyRows) {
      setProgress((p) => ({ ...p, [row.patientId]: { state: 'sending' } }));
      try {
        const res = await OnDemandOrdersService.activateKit(row.patientId);
        if (res.success) {
          setProgress((p) => ({
            ...p,
            [row.patientId]: {
              state: 'success',
              message: res.data?.requisitionId ? `Req #${res.data.requisitionId}` : 'Activated',
            },
          }));
        } else {
          setProgress((p) => ({
            ...p,
            [row.patientId]: { state: 'failed', message: res.message || 'Failed' },
          }));
        }
      } catch (err) {
        setProgress((p) => ({
          ...p,
          [row.patientId]: { state: 'failed', message: err.message || 'Failed' },
        }));
      }
    }
    setSending(false);
    setDone(true);
  };

  const renderStatus = (_, row) => {
    const prog = progress[row.patientId];
    if (prog?.state === 'sending') return <Tag icon={<LoadingOutlined />} color="processing">Sending…</Tag>;
    if (prog?.state === 'success') {
      return (
        <Space size={4}>
          <CheckCircleTwoTone twoToneColor="#52c41a" />
          <span>Activated {prog.message}</span>
        </Space>
      );
    }
    if (prog?.state === 'failed') {
      return (
        <Tooltip title={prog.message}>
          <Space size={4}>
            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
            <span>Failed</span>
          </Space>
        </Tooltip>
      );
    }
    if (row.alreadyActivated) {
      return <Tag color="default">Already activated</Tag>;
    }
    if (!row.ok) {
      return (
        <Tooltip title={`Missing: ${(row.missing || []).join(', ')}`}>
          <Space size={4}>
            <ExclamationCircleTwoTone twoToneColor="#faad14" />
            <span>Blocked</span>
          </Space>
        </Tooltip>
      );
    }
    return <Tag color="blue">Ready</Tag>;
  };

  const columns = [
    { title: 'Member', dataIndex: 'name', key: 'name', render: (n) => n || '—' },
    {
      title: 'Kit code',
      dataIndex: 'activationCode',
      key: 'activationCode',
      render: (c) => c || <span style={{ color: '#ff4d4f' }}>missing</span>,
    },
    { title: 'Status', key: 'status', render: renderStatus },
  ];

  const totalProgress = readyRows.length
    ? Math.round(((sentCount + failedCount) / readyRows.length) * 100)
    : 0;

  return (
    <Modal
      title="Activate kits with Endless Health"
      open={visible}
      onCancel={onClose}
      width={720}
      footer={[
        <Button key="close" onClick={onClose} disabled={sending}>
          {done ? 'Close' : 'Cancel'}
        </Button>,
        !done && (
          <Button
            key="send"
            type="primary"
            loading={sending}
            disabled={validating || readyRows.length === 0}
            onClick={handleSend}
          >
            {`Activate ${readyRows.length} kit${readyRows.length === 1 ? '' : 's'}`}
          </Button>
        ),
      ]}
    >
      {!validating && rows.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type={readyRows.length ? 'info' : 'warning'}
          showIcon
          message={
            done
              ? `Done — ${sentCount} activated, ${failedCount} failed.`
              : `${readyRows.length} ready to activate${
                  blockedCount ? `, ${blockedCount} blocked (missing data or already activated)` : ''
                }.`
          }
        />
      )}

      {(sending || done) && readyRows.length > 0 && (
        <Progress percent={totalProgress} status={failedCount ? 'exception' : 'active'} style={{ marginBottom: 12 }} />
      )}

      <Table
        size="small"
        rowKey="patientId"
        loading={validating}
        columns={columns}
        dataSource={rows}
        pagination={rows.length > 8 ? { pageSize: 8 } : false}
      />
    </Modal>
  );
};

export default BulkKitActivationModal;
